import type { Seat } from '@campfire/engine';
import { CATEGORIES, CATEGORY_LABEL, UPPER, UPPER_BONUS_AT, type Category, type YahtzeeEvent, type YahtzeeView } from '@campfire/game-yahtzee';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { die, randomFace, tumbling } from './dice.ts';

const eventText = (ctx: TableCtx, e: YahtzeeEvent | null, v: YahtzeeView): string => {
  if (!e) return `${name(ctx, v.turn)} ${v.turn === ctx.you ? 'start' : 'starts'}: roll the dice.`;
  if (e.kind === 'roll') return `${name(ctx, e.seat)} rolled (${e.rollsLeft} roll${e.rollsLeft === 1 ? '' : 's'} left).`;
  return `${name(ctx, e.seat)} scored ${e.points} in ${CATEGORY_LABEL[e.category]}${e.joker ? ' (joker)' : ''}${e.bonus ? ' +100 Yahtzee bonus!' : ''}.`;
};

const FIXED: Partial<Record<Category, number>> = { fullHouse: 25, smallStraight: 30, largeStraight: 40, yahtzee: 50 };
/** Faces to show while a new roll tumbles (FR-YAHTZEE-15), or null once it has landed. */
function rollingFaces(ctx: TableCtx, v: YahtzeeView): number[] | null {
  const fresh = v.dice.length > 0 && v.last?.kind === 'roll' && v.last.seat === v.turn;
  return tumbling(ctx, 'yz-roll', `${v.turn}-${v.round}-${v.rollsLeft}-${v.dice.join('')}`, fresh) ? Array.from({ length: 5 }, randomFace) : null;
}

export const yahtzeeUi: GameUi = {
  id: 'yahtzee',
  privateHands: false,
  seatInfo: (ctx, s) => `${(ctx.msg.view as YahtzeeView).totals[s] ?? 0} pts`,
  render(ctx) {
    const v = ctx.msg.view as YahtzeeView;
    const done = v.result !== null;
    // Held dice live on the table, so everyone sees them as they're picked and a reconnect keeps them (FR-YAHTZEE-12).
    const hold = v.held ?? [];
    const rolled = v.dice.length > 0;
    const rolling = rollingFaces(ctx, v);
    const canHold = ctx.myTurn && rolled && v.rollsLeft > 0 && !rolling;
    const toRoll = rolled ? v.dice.length - hold.length : 5;
    const canRoll = ctx.myTurn && v.rollsLeft > 0 && toRoll > 0 && !rolling;
    // Re-rolling every die mid-turn throws the whole hand away: ask for a second tap (FR-YAHTZEE-13).
    const turnKey = `${v.turn}-${v.round}-${v.rollsLeft}`;
    const confirming = ctx.local.get<string>('yz-confirm') === turnKey;
    const needsConfirm = rolled && hold.length === 0;
    const roll = () => {
      if (needsConfirm && !confirming) return ctx.local.set('yz-confirm', turnKey);
      ctx.local.set('yz-confirm', undefined);
      ctx.act({ type: 'roll' });
    };

    const dice = h(
      `div.dice-row${rolling ? '.rolling' : ''}`,
      { 'data-testid': 'dice', 'data-rolling': rolling ? 'true' : undefined },
      rolled
        ? v.dice.map((d, i) =>
            die(rolling && !hold.includes(i) ? (rolling[i] as number) : d, {
              held: hold.includes(i),
              onclick: canHold ? () => (ctx.local.set('yz-confirm', undefined), ctx.act({ type: 'hold', index: i })) : undefined,
              testid: `die-${i}`,
            }),
          )
        : Array.from({ length: 5 }, (_, i) => die(rolling ? (rolling[i] as number) : 0, { testid: `die-${i}` })),
    );

    const open = new Set<Category>(v.legal);
    const cell = (seat: Seat, cat: Category) => {
      const val = v.sheets[seat]?.[cat] ?? null;
      if (val !== null) return h('td.used', { 'data-testid': `cell-${seat}-${cat}` }, val);
      // Fixed-value boxes show what they're worth, greyed and struck through when these dice don't make them (FR-YAHTZEE-14).
      const worth = FIXED[cat];
      const missed = worth !== undefined ? h('span.fixed-miss', { 'aria-label': `${worth} if made, 0 now` }, worth) : null;
      if (seat !== v.turn || done || rolling) return h('td', {}, worth !== undefined && !done ? h('span.fixed-worth', {}, worth) : '');
      const pot = v.potentials?.[cat];
      if (pot === undefined) return h('td', {}, worth !== undefined ? h('span.fixed-worth', {}, worth) : '');
      const shown = pot === 0 && missed ? missed : pot;
      return ctx.myTurn && open.has(cat)
        ? h('td.potential', {}, h('button.score-btn', { 'data-testid': `score-${cat}`, 'data-points': pot, onclick: () => ctx.act({ type: 'score', category: cat }) }, shown))
        : h(`td.potential${open.has(cat) ? '' : '.blocked'}`, { 'data-testid': `pot-${cat}`, 'data-points': pot }, shown);
    };
    const seats = v.sheets.map((_, i) => i);
    const row = (cat: Category) => h('tr', {}, h('th', { scope: 'row' }, CATEGORY_LABEL[cat]), seats.map((s) => cell(s, cat)));
    const sumRow = (label: string, f: (s: Seat) => string | number, testid: string) =>
      h('tr.sum', {}, h('th', { scope: 'row' }, label), seats.map((s) => h('td', { 'data-testid': `${testid}-${s}` }, f(s))));

    const card = h(
      'table.scorecard',
      { 'data-testid': 'scorecard' },
      h('thead', {}, h('tr', {}, h('th', {}, ''), seats.map((s) => h(`th${s === v.turn && !done ? '.on' : ''}`, {}, name(ctx, s))))),
      h(
        'tbody',
        {},
        UPPER.map((c) => row(c)),
        sumRow(`Bonus (${UPPER_BONUS_AT}+ = 35)`, (s) => ((v.upper[s] ?? 0) >= UPPER_BONUS_AT ? 35 : `${v.upper[s] ?? 0}/${UPPER_BONUS_AT}`), 'bonus'),
        CATEGORIES.filter((c) => !(UPPER as readonly string[]).includes(c)).map((c) => row(c)),
        sumRow('Yahtzee bonus', (s) => (v.bonuses[s] ?? 0) * 100, 'ybonus'),
        sumRow('Total', (s) => v.totals[s] ?? 0, 'total'),
      ),
    );

    return h(
      'div.yz-table',
      {},
      h('div.muted', { 'data-testid': 'yz-round' }, `Round ${Math.min(v.round, 13)} of 13`),
      dice,
      h(
        'div.row.actions',
        {},
        h(
          `button.primary.big${confirming ? '.warn' : ''}`,
          { 'data-testid': 'yz-roll', disabled: !canRoll || done, onclick: roll },
          rolling
            ? 'Rolling…'
            : confirming
              ? 'Nothing held: roll all 5 again?'
              : !rolled
                ? 'Roll 5 dice'
                : toRoll === 0
                  ? 'All dice held'
                  : `Roll ${toRoll} ${toRoll === 1 ? 'die' : 'dice'} (${v.rollsLeft} left)`,
        ),
        confirming ? h('button', { 'data-testid': 'yz-roll-cancel', onclick: () => ctx.local.set('yz-confirm', undefined) }, 'Cancel') : null,
      ),
      h('div.event', { 'data-testid': 'event' }, eventText(ctx, v.last, v)),
      ctx.myTurn && !done
        ? h('div.hint', { 'data-testid': 'hint' }, !rolled ? 'Roll to start your turn.' : v.rollsLeft > 0 ? 'Tap the dice you want to keep (they stay put), roll the rest, or tap a score to finish.' : 'Out of rolls: tap a score.')
        : null,
      h('div.scroll-x', {}, card),
    );
  },
};
